import React, { useReducer, createContext, ReactNode } from 'react'
import { updateThemeScheme } from '../utils/theme-settings'

type Theme = 'light' | 'dark' | 'auto'

interface SettingsState {
    theme: Theme
}

interface SettingsContextValue extends SettingsState {
    updateTheme: (theme: Theme) => void
}

const SettingsContext = createContext<SettingsContextValue>({
    theme: 'auto',
    updateTheme: () => {},
})

type SettingsAction = { type: 'UPDATE_THEME'; payload: Theme }

function settingsReducer(state: SettingsState, action: SettingsAction): SettingsState {
    switch (action.type) {
        case 'UPDATE_THEME':
            return { ...state, theme: action.payload }
        default:
            return state
    }
}

function initSettings(): SettingsState {
    const storedTheme = (localStorage.getItem('theme') as Theme) || 'auto'
    return {
        theme: storedTheme,
    }
}

interface SettingsProviderProps {
    children: ReactNode
}

const SettingsProvider = ({ children }: SettingsProviderProps) => {
    const [state, dispatch] = useReducer(settingsReducer, undefined, initSettings)

    const updateTheme = (theme: Theme) => {
        localStorage.setItem('theme', theme)
        dispatch({ type: 'UPDATE_THEME', payload: theme })

        // Apply theme using existing utility
        updateThemeScheme(theme)
    }

    // Apply initial theme on mount
    React.useEffect(() => {
        updateThemeScheme(state.theme)
    }, []) // Empty dependency array - only run once on mount

    return (
        <SettingsContext.Provider
            value={{
                theme: state.theme,
                updateTheme,
            }}
        >
            {children}
        </SettingsContext.Provider>
    )
}

export { SettingsContext, SettingsProvider }
